import { useState, useCallback } from 'react';

interface RewardCardProps {
  title: string;
  subtitle: string;
  totalValue: string;
  totalLabel: string;
  dailyRate: string;
  dailyTotal: string;
  borderColor: string;
  iconBg: string;
  iconColor: string;
  icon: React.ReactNode;
  segments: Array<{ percent: number; color: string }>;
  breakdown: Array<{ label: string; color: string; value: string; percent: number }>;
}

export function RewardCard({
  title,
  subtitle,
  totalValue,
  totalLabel,
  dailyRate,
  dailyTotal,
  borderColor,
  iconBg,
  iconColor,
  icon,
  segments,
  breakdown,
}: RewardCardProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`glass-card p-5 sm:p-10 border-l-[3px] ${borderColor}`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <div className={`w-11 h-11 rounded-ternoa flex items-center justify-center ${iconBg} ${iconColor}`}>
          {icon}
        </div>
        <div>
          <div className="font-display text-xl font-semibold tracking-tight">{title}</div>
          <div className="text-sm text-muted">{subtitle}</div>
        </div>
      </div>

      {/* Value */}
      <div className="font-display text-2xl sm:text-4xl font-bold tracking-tight leading-tight">
        {totalValue} <span className="text-sm sm:text-lg font-medium text-muted">{totalLabel}</span>
      </div>

      {/* Daily rate */}
      <div className="text-sm text-gray-400 mt-1">
        Earning <span className="font-display font-semibold text-gray-300">~{dailyRate}</span> of{' '}
        {dailyTotal} distributed
      </div>

      {/* Stacked bar */}
      <div className="flex h-1.5 rounded-full overflow-hidden mt-4 gap-0.5">
        {segments.map((seg, i) => (
          <div
            key={i}
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${seg.percent}%`, background: seg.color }}
          />
        ))}
      </div>

      {/* Breakdown toggle */}
      <div className="mt-6 pt-6 border-t border-border-subtle">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center justify-between w-full text-sm text-muted hover:text-gray-400 transition-colors bg-transparent border-none cursor-pointer font-body"
        >
          View breakdown
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        <div
          className={`overflow-hidden transition-all duration-400 ${
            open ? 'max-h-[300px] mt-3' : 'max-h-0'
          }`}
        >
          {breakdown.map((item) => (
            <div key={item.label} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span className="w-2 h-2 rounded-sm" style={{ background: item.color }} />
                {item.label}
              </div>
              <div className="flex-1 h-1 bg-border-subtle rounded mx-4 overflow-hidden">
                <div
                  className="h-full rounded transition-all duration-700"
                  style={{ width: `${item.percent}%`, background: item.color }}
                />
              </div>
              <span className="font-display text-sm font-semibold text-gray-300 min-w-[56px] text-right">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface ShieldCardProps {
  totalShield: number;
  activeShield: number;
  cifersRedeemed: number;
  dailyRate: number;
  breakdown: { stablecoin: number; caps: number; social: number; hold: number };
  onRedeem: () => Promise<{ success: boolean; error?: string }>;
}

export function ShieldCard({ totalShield, activeShield, cifersRedeemed, dailyRate, breakdown, onRedeem }: ShieldCardProps) {
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);

  const canRedeem = activeShield >= 100;
  const ciferProgress = (activeShield % 100) / 100;

  const handleRedeem = useCallback(async () => {
    setRedeeming(true);
    setRedeemError(null);
    const result = await onRedeem();
    if (!result.success) {
      setRedeemError(result.error || 'Failed to redeem');
    }
    setRedeeming(false);
  }, [onRedeem]);

  return (
    <div className="glass-card p-5 sm:p-10 border-l-[3px] border-l-shield">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <div className="w-11 h-11 rounded-ternoa flex items-center justify-center bg-shield-light text-shield">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </div>
        <div>
          <div className="font-display text-xl font-semibold tracking-tight">Shield Score</div>
          <div className="text-sm text-muted">100 Shield = 1 CIFER</div>
        </div>
      </div>

      {/* Active Shield (main value) */}
      <div className="font-display text-2xl sm:text-4xl font-bold tracking-tight leading-tight">
        {activeShield.toLocaleString(undefined, { maximumFractionDigits: 1 })} <span className="text-sm sm:text-lg font-medium text-muted">active pts</span>
      </div>

      {/* Total Shield */}
      <div className="text-sm text-gray-400 mt-1">
        Total earned: <span className="font-display font-semibold text-gray-300">{totalShield.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span> pts
        {cifersRedeemed > 0 && (
          <span className="ml-2 text-accent">({cifersRedeemed} CIFER redeemed)</span>
        )}
      </div>

      {/* Daily rate */}
      <div className="text-sm text-gray-400 mt-1">
        Earning <span className="font-display font-semibold text-gray-300">~{dailyRate.toFixed(1)} pts/day</span> of 30 distributed
      </div>

      {/* Progress to next CIFER */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-muted mb-1.5">
          <span>Progress to next CIFER</span>
          <span className="font-display font-semibold">{(activeShield % 100).toFixed(1)} / 100</span>
        </div>
        <div className="h-2 rounded-full overflow-hidden bg-border-subtle">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(ciferProgress * 100, 100)}%`,
              background: 'linear-gradient(90deg, #A855F7, #6366F1)',
            }}
          />
        </div>
      </div>

      {/* Redeem button */}
      <div className="mt-4">
        <button
          onClick={handleRedeem}
          disabled={!canRedeem || redeeming}
          className={`w-full py-3 px-4 rounded-ternoa font-display font-semibold text-sm tracking-wide transition-all border-none cursor-pointer ${
            canRedeem && !redeeming
              ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-400 hover:to-indigo-400 shadow-lg shadow-purple-500/25'
              : 'bg-glass-bg text-muted border border-border-subtle cursor-not-allowed opacity-50'
          }`}
        >
          {redeeming ? 'Redeeming...' : canRedeem ? 'Redeem CIFER' : `Need ${(100 - (activeShield % 100)).toFixed(1)} more pts`}
        </button>
        {redeemError && (
          <div className="text-xs text-red-400 mt-2">{redeemError}</div>
        )}
      </div>

      {/* Breakdown */}
      <div className="mt-6 pt-6 border-t border-border-subtle">
        <div className="flex h-1.5 rounded-full overflow-hidden mb-4 gap-0.5">
          <div className="h-full rounded-full" style={{ width: '40%', background: '#2775CA' }} />
          <div className="h-full rounded-full" style={{ width: '25%', background: '#F59E0B' }} />
          <div className="h-full rounded-full" style={{ width: '20%', background: '#3B82F6' }} />
          <div className="h-full rounded-full" style={{ width: '15%', background: '#888888' }} />
        </div>
        <div className="space-y-2">
          {[
            { label: 'Stablecoin balance', color: '#2775CA', value: breakdown.stablecoin },
            { label: '$CAPS holdings', color: '#F59E0B', value: breakdown.caps },
            { label: 'Social activity', color: '#3B82F6', value: breakdown.social },
            { label: 'Hold duration bonus', color: '#888888', value: breakdown.hold },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span className="w-2 h-2 rounded-sm" style={{ background: item.color }} />
                {item.label}
              </div>
              <span className="font-display text-xs font-semibold text-gray-300">
                {(item.value * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function KatCard({ total, dailyRate }: { total: number; dailyRate: number }) {
  return (
    <div className="glass-card p-5 sm:p-10 border-l-[3px] border-l-kat">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <div className="w-11 h-11 rounded-ternoa flex items-center justify-center bg-kat-light text-kat">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
        </div>
        <div>
          <div className="font-display text-xl font-semibold tracking-tight">$KAT Airdrop</div>
          <div className="text-sm text-muted">Earned through social engagement</div>
        </div>
      </div>

      {/* Value */}
      <div className="font-display text-2xl sm:text-4xl font-bold tracking-tight leading-tight">
        {total.toLocaleString(undefined, { maximumFractionDigits: 1 })} <span className="text-sm sm:text-lg font-medium text-muted">KAT</span>
      </div>

      {/* Daily rate */}
      <div className="text-sm text-gray-400 mt-1">
        {dailyRate > 0 ? (
          <>Earned <span className="font-display font-semibold text-gray-300">~{dailyRate.toFixed(1)}</span> KAT today</>
        ) : (
          <>Complete social tasks below to earn KAT</>
        )}
      </div>

      {/* Social activity bar */}
      <div className="flex h-1.5 rounded-full overflow-hidden mt-4 gap-0.5">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: '100%', background: 'linear-gradient(90deg, #ec4899, #a855f7, #3b82f6)' }}
        />
      </div>

      {/* Info */}
      <div className="mt-6 pt-6 border-t border-border-subtle">
        <div className="flex items-center gap-2 text-sm text-muted">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-kat">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
          </svg>
          300 KAT distributed daily — proportional to your social activity score
        </div>
      </div>
    </div>
  );
}
